import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  ArrowLeft,
  Save,
  Globe,
  Eye,
  CheckCircle2,
  Clock,
  RefreshCw,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  Copy,
  Type,
  AlignLeft,
  List,
  ListOrdered,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  Minus,
  Quote as QuoteIcon,
  MoveVertical,
  Settings,
  Palette,
  Search as SearchIcon,
  Image,
  X,
  Table2,
  Bold,
  Italic,
  Link2,
  Repeat,
  GripVertical,
} from 'lucide-react';
import {
  useAdminPage,
  updateCmsPage,
  publishCmsPage,
  unpublishCmsPage,
  checkSlugAvailable,
} from '../hooks/useAdminPages';
import {
  type ContentBlock,
  type BlockType,
  type CmsPage,
  generateBlockId,
  createBlock,
  isSlugReserved,
  isSlugValid,
} from '@/types/cms';

type EditLanguage = 'en' | 'fr';
type SettingsTab = 'page' | 'appearance' | 'seo';

// ── Block type definitions for the add-block menu ──
const BLOCK_OPTIONS: { type: BlockType; label: string; icon: typeof Type; description: string }[] =
  [
    { type: 'heading', label: 'Heading', icon: Type, description: 'Section title (H2, H3, H4)' },
    { type: 'paragraph', label: 'Paragraph', icon: AlignLeft, description: 'Text content' },
    { type: 'list', label: 'Bullet List', icon: List, description: 'Unordered list' },
    { type: 'callout', label: 'Callout', icon: Info, description: 'Highlighted message box' },
    { type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal separator' },
    { type: 'quote', label: 'Quote', icon: QuoteIcon, description: 'Blockquote with author' },
    { type: 'spacer', label: 'Spacer', icon: MoveVertical, description: 'Vertical spacing' },
    { type: 'image', label: 'Image', icon: Image, description: 'Image from URL' },
    { type: 'table', label: 'Table', icon: Table2, description: 'Data table with headers' },
  ];

export default function CmsEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { page, isLoading, error } = useAdminPage(id);

  // ── Editor state ──
  const [lang, setLang] = useState<EditLanguage>('fr');
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('page');
  const [showSettings, setShowSettings] = useState(true);
  const [addBlockIndex, setAddBlockIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [slugInput, setSlugInput] = useState('');
  const [slugError, setSlugError] = useState('');

  // ── Editable page fields ──
  const [titleEn, setTitleEn] = useState('');
  const [titleFr, setTitleFr] = useState('');
  const [subtitleEn, setSubtitleEn] = useState('');
  const [subtitleFr, setSubtitleFr] = useState('');
  const [blocksEn, setBlocksEn] = useState<ContentBlock[]>([]);
  const [blocksFr, setBlocksFr] = useState<ContentBlock[]>([]);
  const [metaEn, setMetaEn] = useState('');
  const [metaFr, setMetaFr] = useState('');
  const [heroFrom, setHeroFrom] = useState('#16c47f');
  const [heroTo, setHeroTo] = useState('transparent');
  const [showHero, setShowHero] = useState(true);
  const [showHeader, setShowHeader] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [heroMetaEn, setHeroMetaEn] = useState('');
  const [heroMetaFr, setHeroMetaFr] = useState('');
  const [status, setStatus] = useState<CmsPage['status']>('draft');

  // ── Load page data ──
  useEffect(() => {
    if (!page) return;
    setTitleEn(page.title_en);
    setTitleFr(page.title_fr);
    setSubtitleEn(page.subtitle_en);
    setSubtitleFr(page.subtitle_fr);
    setBlocksEn(page.content_en || []);
    setBlocksFr(page.content_fr || []);
    setMetaEn(page.meta_description_en);
    setMetaFr(page.meta_description_fr);
    setHeroFrom(page.hero_gradient_from);
    setHeroTo(page.hero_gradient_to);
    setShowHero(page.show_hero);
    setShowHeader(page.show_header);
    setShowFooter(page.show_footer);
    setHeroMetaEn(page.hero_meta_en || '');
    setHeroMetaFr(page.hero_meta_fr || '');
    setSlugInput(page.slug);
    setStatus(page.status);
  }, [page]);

  // ── Get current language content ──
  const title = lang === 'en' ? titleEn : titleFr;
  const setTitle = lang === 'en' ? setTitleEn : setTitleFr;
  const subtitle = lang === 'en' ? subtitleEn : subtitleFr;
  const setSubtitle = lang === 'en' ? setSubtitleEn : setSubtitleFr;
  const blocks = lang === 'en' ? blocksEn : blocksFr;
  const setBlocks = lang === 'en' ? setBlocksEn : setBlocksFr;

  // ── Block operations ──
  const addBlock = (type: BlockType, index: number) => {
    const newBlock = createBlock(type);
    const updated = [...blocks];
    updated.splice(index, 0, newBlock);
    setBlocks(updated);
    setAddBlockIndex(null);
  };

  const updateBlock = useCallback(
    (blockId: string, changes: Partial<ContentBlock>) => {
      setBlocks(
        (prev) => prev.map((b) => (b.id === blockId ? { ...b, ...changes } : b)) as ContentBlock[]
      );
    },
    [setBlocks]
  );

  const removeBlock = (blockId: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
  };

  const moveBlock = (blockId: string, direction: 'up' | 'down') => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === blockId);
      if (idx < 0) return prev;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });
  };

  const duplicateBlock = (blockId: string) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === blockId);
      if (idx < 0) return prev;
      const original = prev[idx];
      const clone = { ...original, id: generateBlockId() };
      if (original.type === 'list') {
        (clone as typeof original).items = [...original.items];
      }
      if (original.type === 'table') {
        (clone as typeof original).rows = original.rows.map((r) => [...r]);
        (clone as typeof original).headers = [...original.headers];
      }
      const copy = [...prev];
      copy.splice(idx + 1, 0, clone as ContentBlock);
      return copy;
    });
  };

  const convertBlock = (blockId: string, toType: BlockType) => {
    setBlocks((prev) =>
      prev.map((b) => {
        if (b.id !== blockId) return b;
        // Extract text from current block
        let text = '';
        if ('text' in b) text = b.text;
        else if ('items' in b) text = b.items.join('\n');
        // Build new block preserving id
        const base = createBlock(toType);
        const converted = { ...base, id: b.id } as ContentBlock;
        // Inject text into target
        if ('text' in converted) {
          (converted as { text: string }).text = text;
        }
        if (toType === 'list') {
          (converted as { items: string[] }).items = text ? text.split('\n').filter(Boolean) : [''];
        }
        return converted;
      })
    );
  };

  // ── Save ──
  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      // Validate slug
      if (slugInput !== page?.slug) {
        if (!isSlugValid(slugInput)) {
          setSlugError('Invalid path format');
          setSaving(false);
          return;
        }
        if (isSlugReserved(slugInput)) {
          setSlugError('This path conflicts with an existing app route');
          setSaving(false);
          return;
        }
        const available = await checkSlugAvailable(slugInput, id);
        if (!available) {
          setSlugError('This path is already in use');
          setSaving(false);
          return;
        }
      }

      await updateCmsPage(id, {
        slug: slugInput,
        title_en: titleEn,
        title_fr: titleFr,
        subtitle_en: subtitleEn,
        subtitle_fr: subtitleFr,
        content_en: blocksEn,
        content_fr: blocksFr,
        meta_description_en: metaEn,
        meta_description_fr: metaFr,
        hero_gradient_from: heroFrom,
        hero_gradient_to: heroTo,
        show_hero: showHero,
        show_header: showHeader,
        show_footer: showFooter,
        hero_meta_en: heroMetaEn,
        hero_meta_fr: heroMetaFr,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  // ── Publish / Unpublish ──
  const handleTogglePublish = async () => {
    if (!id) return;
    setPublishing(true);
    try {
      // Save first
      await handleSave();
      if (status === 'published') {
        await unpublishCmsPage(id);
        setStatus('draft');
      } else {
        await publishCmsPage(id);
        setStatus('published');
      }
    } catch {
      // silent
    } finally {
      setPublishing(false);
    }
  };

  // ── Loading / Error states ──
  if (isLoading) {
    return (
      <AdminLayout title="Loading..." subtitle="Please wait">
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !page) {
    return (
      <AdminLayout title="Error" subtitle="Page not found">
        <div className="text-center py-20">
          <p className="text-red-500 mb-4">{error || 'Page not found'}</p>
          <button
            onClick={() => navigate('/admin/pages')}
            className="text-sm text-[#16c47f] hover:underline"
          >
            Back to pages
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title=""
      action={
        <div className="flex items-center gap-3 w-full">
          {/* Back button */}
          <button
            onClick={() => navigate('/admin/pages')}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Page title inline */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
                {titleEn || titleFr || 'Untitled Page'}
              </h1>
              {status === 'published' ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-[#16c47f]/10 text-[#16c47f]">
                  <CheckCircle2 className="w-3 h-3" /> Published
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <Clock className="w-3 h-3" /> Draft
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">/p/{slugInput}</p>
          </div>

          {/* Language toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-xl p-0.5">
            {(['fr', 'en'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  lang === l
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <img
                  src={`https://flagcdn.com/${l === 'en' ? 'us' : 'fr'}.svg`}
                  alt={l.toUpperCase()}
                  className="inline-block w-4 h-3 rounded-sm mr-1"
                />
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Settings toggle */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2 rounded-lg transition-all ${
              showSettings
                ? 'bg-[#16c47f]/10 text-[#16c47f]'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <Settings className="w-5 h-5" />
          </button>

          {/* Preview */}
          {status === 'published' && (
            <a
              href={`/p/${page.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-all"
            >
              <Eye className="w-5 h-5" />
            </a>
          )}

          {/* Save */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 transition-colors"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : saved ? (
              <CheckCircle2 className="w-4 h-4 text-[#16c47f]" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saved ? 'Saved!' : 'Save'}
          </button>

          {/* Publish */}
          <button
            onClick={handleTogglePublish}
            disabled={publishing}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              status === 'published'
                ? 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-500/20'
                : 'bg-[#16c47f] hover:bg-[#12a868] text-white'
            }`}
          >
            {publishing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : status === 'published' ? (
              <>
                <Clock className="w-4 h-4" /> Unpublish
              </>
            ) : (
              <>
                <Globe className="w-4 h-4" /> Publish
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="flex gap-6">
        {/* ═══ Main Block Editor ═══ */}
        <div className={`flex-1 min-w-0 pl-12 ${showSettings ? 'max-w-[calc(100%-320px)]' : ''}`}>
          {/* Title & Subtitle */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 mb-4">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={lang === 'en' ? 'Page Title' : 'Titre de la page'}
              className="w-full text-2xl font-bold text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 bg-transparent border-none outline-none mb-2"
            />
            <input
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder={lang === 'en' ? 'Page subtitle (optional)' : 'Sous-titre (optionnel)'}
              className="w-full text-sm text-gray-500 dark:text-gray-400 placeholder-gray-300 dark:placeholder-gray-600 bg-transparent border-none outline-none"
            />
          </div>

          {/* Add block button at top */}
          <AddBlockButton
            index={0}
            isOpen={addBlockIndex === 0}
            onToggle={() => setAddBlockIndex(addBlockIndex === 0 ? null : 0)}
            onAdd={addBlock}
          />

          {/* Blocks */}
          {blocks.map((block, index) => (
            <div key={block.id}>
              <BlockEditor
                block={block}
                index={index}
                total={blocks.length}
                onUpdate={updateBlock}
                onRemove={removeBlock}
                onMove={moveBlock}
                onDuplicate={duplicateBlock}
                onConvert={convertBlock}
                lang={lang}
              />
              <AddBlockButton
                index={index + 1}
                isOpen={addBlockIndex === index + 1}
                onToggle={() => setAddBlockIndex(addBlockIndex === index + 1 ? null : index + 1)}
                onAdd={addBlock}
              />
            </div>
          ))}

          {blocks.length === 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-dashed border-gray-100 dark:border-gray-700/50 p-12 text-center mb-4">
              <AlignLeft className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium mb-1">No content yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Click the <span className="text-[#16c47f] font-semibold">+</span> button above to
                add your first block
              </p>
            </div>
          )}
        </div>

        {/* ═══ Settings Panel ═══ */}
        {showSettings && (
          <div className="w-80 flex-shrink-0">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 sticky top-20">
              {/* Settings tabs */}
              <div className="flex border-b border-gray-100 dark:border-gray-700 rounded-t-2xl overflow-hidden">
                {(
                  [
                    { id: 'page' as const, label: 'Page', icon: Settings },
                    { id: 'appearance' as const, label: 'Style', icon: Palette },
                    { id: 'seo' as const, label: 'SEO', icon: SearchIcon },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSettingsTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-medium transition-all border-b-2 ${
                      settingsTab === tab.id
                        ? 'border-[#16c47f] text-[#16c47f]'
                        : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
                    }`}
                  >
                    <tab.icon className="w-3.5 h-3.5" />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* ── Page Settings Tab ── */}
                {settingsTab === 'page' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                        URL Path
                      </label>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-400 bg-gray-50 dark:bg-gray-700 px-2 py-2 rounded-l-lg border border-r-0 border-gray-200 dark:border-gray-600 select-none">
                          /p/
                        </span>
                        <input
                          type="text"
                          value={slugInput}
                          onChange={(e) => {
                            const val = e.target.value.toLowerCase().replace(/[^a-z0-9/-]/g, '');
                            setSlugInput(val);
                            setSlugError('');
                          }}
                          disabled={page.is_system}
                          className={`flex-1 px-2 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-r-lg text-xs text-gray-900 dark:text-white focus:ring-1 focus:ring-[#16c47f] outline-none transition-all ${page.is_system ? 'opacity-50 cursor-not-allowed' : ''}`}
                        />
                      </div>
                      {page.is_system && (
                        <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 flex items-center gap-1">
                          System page — slug cannot be changed
                        </p>
                      )}
                      {slugError && <p className="text-xs text-red-500 mt-1">{slugError}</p>}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                        Status
                      </label>
                      <div className="flex items-center gap-2">
                        {status === 'published' ? (
                          <span className="flex items-center gap-1.5 text-sm text-[#16c47f] font-medium">
                            <CheckCircle2 className="w-4 h-4" /> Published
                          </span>
                        ) : status === 'archived' ? (
                          <span className="flex items-center gap-1.5 text-sm text-gray-500 font-medium">
                            <Clock className="w-4 h-4" /> Archived
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-sm text-amber-500 font-medium">
                            <Clock className="w-4 h-4" /> Draft
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                        Template
                      </label>
                      <p className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                        {page.template}
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                        Hero Meta Text
                      </label>
                      <input
                        type="text"
                        value={lang === 'fr' ? heroMetaFr : heroMetaEn}
                        onChange={(e) =>
                          lang === 'fr'
                            ? setHeroMetaFr(e.target.value)
                            : setHeroMetaEn(e.target.value)
                        }
                        placeholder="e.g. Dernière mise à jour : Mars 2026"
                        className="w-full px-2 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-[#16c47f] outline-none transition-all"
                      />
                      <p className="text-[10px] text-gray-400 mt-1">
                        Shown below subtitle in hero section
                      </p>
                    </div>

                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Created: {new Date(page.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        Updated: {new Date(page.updated_at).toLocaleDateString()}
                      </p>
                      {page.published_at && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Published: {new Date(page.published_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* ── Appearance Tab ── */}
                {settingsTab === 'appearance' && (
                  <>
                    <ToggleField
                      label="Show Hero Section"
                      checked={showHero}
                      onChange={setShowHero}
                    />

                    {showHero && (
                      <>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Hero Gradient Start
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={heroFrom}
                              onChange={(e) => setHeroFrom(e.target.value)}
                              className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={heroFrom}
                              onChange={(e) => setHeroFrom(e.target.value)}
                              className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white outline-none"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                            Hero Gradient End
                          </label>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-8 h-8 rounded-lg border border-gray-200 dark:border-gray-600"
                              style={{
                                backgroundColor: heroTo === 'transparent' ? 'transparent' : heroTo,
                              }}
                            />
                            <input
                              type="text"
                              value={heroTo}
                              onChange={(e) => setHeroTo(e.target.value)}
                              className="flex-1 px-2 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white outline-none"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <ToggleField
                      label="Show Header"
                      checked={showHeader}
                      onChange={setShowHeader}
                    />
                    <ToggleField
                      label="Show Footer"
                      checked={showFooter}
                      onChange={setShowFooter}
                    />

                    {/* Style Preview */}
                    <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Preview
                      </label>
                      <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
                        {showHeader && (
                          <div className="h-6 bg-white dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex items-center px-2">
                            <div className="w-8 h-2 bg-gray-200 dark:bg-gray-600 rounded" />
                          </div>
                        )}
                        {showHero && (
                          <div
                            className="h-16 flex items-center justify-center"
                            style={{
                              background: `linear-gradient(to bottom, ${heroFrom}20, ${heroTo === 'transparent' ? 'transparent' : heroTo + '20'})`,
                            }}
                          >
                            <div className="w-20 h-2 bg-gray-300 dark:bg-gray-500 rounded" />
                          </div>
                        )}
                        <div className="h-12 bg-white dark:bg-gray-800 p-2 space-y-1">
                          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-600 rounded" />
                          <div className="w-full h-1 bg-gray-100 dark:bg-gray-700 rounded" />
                          <div className="w-3/4 h-1 bg-gray-100 dark:bg-gray-700 rounded" />
                        </div>
                        {showFooter && (
                          <div className="h-6 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex items-center justify-center">
                            <div className="w-8 h-1.5 bg-gray-200 dark:bg-gray-600 rounded" />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* ── SEO Tab ── */}
                {settingsTab === 'seo' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                        Meta Description (EN)
                      </label>
                      <textarea
                        value={metaEn}
                        onChange={(e) => setMetaEn(e.target.value)}
                        rows={3}
                        maxLength={160}
                        placeholder="Brief page description for search engines..."
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-[#16c47f] outline-none transition-all resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right">{metaEn.length}/160</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                        Meta Description (FR)
                      </label>
                      <textarea
                        value={metaFr}
                        onChange={(e) => setMetaFr(e.target.value)}
                        rows={3}
                        maxLength={160}
                        placeholder="Description courte pour les moteurs de recherche..."
                        className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:ring-1 focus:ring-[#16c47f] outline-none transition-all resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right">{metaFr.length}/160</p>
                    </div>

                    {/* SEO Preview */}
                    <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                      <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Search Preview
                      </label>
                      <div className="bg-white dark:bg-gray-700 rounded-xl p-3 border border-gray-200 dark:border-gray-600">
                        <p className="text-sm text-blue-600 dark:text-blue-400 font-medium truncate">
                          {titleEn || 'Page Title'} — clevia
                        </p>
                        <p className="text-xs text-[#16c47f] truncate mt-0.5">
                          cleviaapp.com/p/{slugInput}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                          {metaEn || 'No description set. Add a meta description for better SEO.'}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// ══════════════════════════════════════════════
// Sub-components
// ══════════════════════════════════════════════

function ToggleField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <div
        className={`relative w-9 h-5 rounded-full transition-colors ${
          checked ? 'bg-[#16c47f]' : 'bg-gray-300 dark:bg-gray-600'
        }`}
        onClick={() => onChange(!checked)}
      >
        <div
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : ''
          }`}
        />
      </div>
    </label>
  );
}

function AddBlockButton({
  index,
  isOpen,
  onToggle,
  onAdd,
}: {
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  onAdd: (type: BlockType, index: number) => void;
}) {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(
    () =>
      search
        ? BLOCK_OPTIONS.filter(
            (o) =>
              o.label.toLowerCase().includes(search.toLowerCase()) ||
              o.description.toLowerCase().includes(search.toLowerCase())
          )
        : BLOCK_OPTIONS,
    [search]
  );

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen]);

  return (
    <div className="relative group py-1">
      <div className="flex items-center justify-center">
        <div className="flex-1 h-px bg-transparent group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors" />
        <button
          onClick={onToggle}
          className={`p-1 rounded-full transition-all ${
            isOpen
              ? 'bg-[#16c47f] text-white shadow-sm rotate-45'
              : 'text-gray-300 dark:text-gray-600 hover:text-[#16c47f] hover:bg-[#16c47f]/5 opacity-0 group-hover:opacity-100'
          }`}
        >
          <Plus className="w-4 h-4" />
        </button>
        <div className="flex-1 h-px bg-transparent group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors" />
      </div>

      {isOpen && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-50 w-72 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 shadow-xl p-2 animate-in fade-in slide-in-from-top-2 duration-150">
          <div className="relative mb-2">
            <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search blocks..."
              className="w-full pl-7 pr-2 py-1.5 text-xs bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg outline-none focus:border-[#16c47f] focus:ring-1 focus:ring-[#16c47f]/20 text-gray-700 dark:text-gray-300 placeholder-gray-400"
            />
          </div>
          <div className="grid grid-cols-2 gap-1 max-h-60 overflow-y-auto">
            {filtered.map((opt) => (
              <button
                key={opt.type}
                onClick={() => onAdd(opt.type, index)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-left hover:bg-[#16c47f]/5 dark:hover:bg-[#16c47f]/10 transition-all group/item"
              >
                <opt.icon className="w-4 h-4 text-gray-400 group-hover/item:text-[#16c47f] transition-colors flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                    {opt.label}
                  </p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">{opt.description}</p>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-2 text-xs text-gray-400 text-center py-3">No blocks found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BlockEditor({
  block,
  index,
  total,
  onUpdate,
  onRemove,
  onMove,
  onDuplicate,
  onConvert,
  lang,
}: {
  block: ContentBlock;
  index: number;
  total: number;
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: 'up' | 'down') => void;
  onDuplicate: (id: string) => void;
  onConvert: (id: string, toType: BlockType) => void;
  lang: EditLanguage;
}) {
  const [focused, setFocused] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const convertRef = useRef<HTMLDivElement>(null);

  // Close convert menu when clicking outside
  useEffect(() => {
    if (!showConvert) return;
    const handler = (e: MouseEvent) => {
      if (convertRef.current && !convertRef.current.contains(e.target as Node)) {
        setShowConvert(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showConvert]);

  const blockLabel = BLOCK_OPTIONS.find((o) => o.type === block.type);

  return (
    <div
      className={`group relative bg-white dark:bg-gray-800 rounded-xl border transition-all duration-200 mb-1 ${
        focused
          ? 'border-[#16c47f]/50 ring-2 ring-[#16c47f]/10 shadow-sm'
          : 'border-gray-100 dark:border-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
      }`}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      {/* Block type label */}
      <div className="absolute left-3 -top-2.5 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white dark:bg-gray-800 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider border border-gray-100 dark:border-gray-700/50 rounded">
          {blockLabel && <blockLabel.icon className="w-2.5 h-2.5" />}
          {blockLabel?.label || block.type}
        </span>
      </div>

      {/* Block toolbar — left side: drag + reorder */}
      <div className="absolute -left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-200 flex flex-col gap-0.5">
        <button
          onClick={() => onMove(block.id, 'up')}
          disabled={index === 0}
          className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <div className="p-1 text-gray-300 dark:text-gray-600 cursor-grab">
          <GripVertical className="w-3.5 h-3.5" />
        </div>
        <button
          onClick={() => onMove(block.id, 'down')}
          disabled={index === total - 1}
          className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Block actions — top right */}
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center gap-0.5 z-10">
        {/* Convert block type */}
        <div className="relative" ref={convertRef}>
          <button
            onClick={() => setShowConvert(!showConvert)}
            className="p-1 rounded text-gray-400 hover:text-[#16c47f] hover:bg-[#16c47f]/5 transition-colors"
            title="Convert to..."
          >
            <Repeat className="w-3.5 h-3.5" />
          </button>
          {showConvert && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700/50 shadow-xl py-1 z-50 max-h-72 overflow-y-auto">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-1 sticky top-0 bg-white dark:bg-gray-800">
                Convert to
              </p>
              {BLOCK_OPTIONS.filter((o) => o.type !== block.type).map((opt) => (
                <button
                  key={opt.type}
                  onClick={() => {
                    onConvert(block.id, opt.type);
                    setShowConvert(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <opt.icon className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-600 dark:text-gray-300">{opt.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={() => onDuplicate(block.id)}
          className="p-1 rounded text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors"
          title="Duplicate"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onRemove(block.id)}
          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Block content */}
      <div className="p-4">
        {block.type === 'heading' && (
          <HeadingBlockEditor block={block} onUpdate={onUpdate} lang={lang} />
        )}
        {block.type === 'paragraph' && (
          <ParagraphBlockEditor
            block={block}
            onUpdate={onUpdate}
            onConvert={(type) => onConvert(block.id, type)}
            lang={lang}
          />
        )}
        {block.type === 'list' && <ListBlockEditor block={block} onUpdate={onUpdate} lang={lang} />}
        {block.type === 'callout' && (
          <CalloutBlockEditor block={block} onUpdate={onUpdate} lang={lang} />
        )}
        {block.type === 'divider' && <DividerBlockEditor />}
        {block.type === 'quote' && (
          <QuoteBlockEditor block={block} onUpdate={onUpdate} lang={lang} />
        )}
        {block.type === 'spacer' && <SpacerBlockEditor block={block} onUpdate={onUpdate} />}
        {block.type === 'image' && (
          <ImageBlockEditor block={block} onUpdate={onUpdate} lang={lang} />
        )}
        {block.type === 'table' && <TableBlockEditor block={block} onUpdate={onUpdate} />}
      </div>
    </div>
  );
}

// ── Individual Block Editors ──

function HeadingBlockEditor({
  block,
  onUpdate,
  lang,
}: {
  block: ContentBlock & { type: 'heading' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
  lang: EditLanguage;
}) {
  const sizeClass = {
    2: 'text-2xl font-bold',
    3: 'text-lg font-bold',
    4: 'text-base font-semibold',
  }[block.level];

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
          {([2, 3, 4] as const).map((lvl) => (
            <button
              key={lvl}
              onClick={() => onUpdate(block.id, { level: lvl })}
              className={`px-2 py-0.5 rounded-md text-xs font-medium transition-all ${
                block.level === lvl
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              H{lvl}
            </button>
          ))}
        </div>
      </div>
      <input
        type="text"
        value={block.text}
        onChange={(e) => onUpdate(block.id, { text: e.target.value })}
        placeholder={lang === 'en' ? 'Heading text...' : 'Texte du titre...'}
        className={`w-full bg-transparent border-none outline-none text-gray-900 dark:text-white placeholder-gray-300 dark:placeholder-gray-600 ${sizeClass}`}
      />
    </div>
  );
}

// ── Text Formatting Toolbar ──

function TextToolbar({
  textareaRef,
  value,
  onChange,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (newValue: string) => void;
}) {
  const wrapSelection = (before: string, after: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    const newText = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(newText);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + before.length, end + before.length);
    });
  };

  const insertLink = () => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.slice(start, end);
    const linkText = selected || 'text';
    const newText = value.slice(0, start) + `[${linkText}](url)` + value.slice(end);
    onChange(newText);
    requestAnimationFrame(() => {
      el.focus();
      const urlStart = start + linkText.length + 3;
      el.setSelectionRange(urlStart, urlStart + 3);
    });
  };

  return (
    <div className="flex items-center gap-0.5 mb-1">
      <button
        type="button"
        onClick={() => wrapSelection('**', '**')}
        className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Bold (**text**)"
      >
        <Bold className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => wrapSelection('*', '*')}
        className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Italic (*text*)"
      >
        <Italic className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={insertLink}
        className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Link [text](url)"
      >
        <Link2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ParagraphBlockEditor({
  block,
  onUpdate,
  onConvert,
  lang,
}: {
  block: ContentBlock & { type: 'paragraph' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
  onConvert: (type: BlockType) => void;
  lang: EditLanguage;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);

  const filteredOptions = useMemo(() => {
    if (slashQuery === null) return [];
    const q = slashQuery.toLowerCase();
    return BLOCK_OPTIONS.filter((o) => o.label.toLowerCase().includes(q) || o.type.includes(q));
  }, [slashQuery]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    onUpdate(block.id, { text: val });
    // Detect slash command at beginning of text
    if (val.startsWith('/')) {
      setSlashQuery(val.slice(1));
      setSlashIndex(0);
    } else {
      setSlashQuery(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (slashQuery === null || filteredOptions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSlashIndex((i) => Math.min(i + 1, filteredOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSlashIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onUpdate(block.id, { text: '' });
      onConvert(filteredOptions[slashIndex].type);
      setSlashQuery(null);
    } else if (e.key === 'Escape') {
      setSlashQuery(null);
    }
  };

  return (
    <div className="relative">
      <TextToolbar
        textareaRef={textareaRef}
        value={block.text}
        onChange={(text) => onUpdate(block.id, { text })}
      />
      <textarea
        ref={textareaRef}
        value={block.text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={
          lang === 'en'
            ? 'Write text or type / to change block type...'
            : 'Écrivez du texte ou tapez / pour changer de type...'
        }
        className="w-full bg-transparent border-none outline-none text-gray-700 dark:text-gray-300 placeholder-gray-300 dark:placeholder-gray-600 text-sm leading-relaxed resize-none min-h-[60px]"
        rows={Math.max(2, block.text.split('\n').length)}
      />
      {/* Slash command menu */}
      {slashQuery !== null && filteredOptions.length > 0 && (
        <div className="absolute left-0 top-full mt-1 z-50 w-64 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 shadow-xl p-1.5 animate-in fade-in slide-in-from-top-2 duration-150">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2 py-1">
            Turn into
          </p>
          {filteredOptions.map((opt, i) => (
            <button
              key={opt.type}
              onMouseDown={(e) => {
                e.preventDefault();
                onUpdate(block.id, { text: '' });
                onConvert(opt.type);
                setSlashQuery(null);
              }}
              className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-colors ${
                i === slashIndex
                  ? 'bg-[#16c47f]/10 text-[#16c47f]'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
            >
              <opt.icon
                className={`w-4 h-4 ${i === slashIndex ? 'text-[#16c47f]' : 'text-gray-400'}`}
              />
              <div>
                <p className="text-xs font-medium text-gray-700 dark:text-gray-200">{opt.label}</p>
                <p className="text-[10px] text-gray-400">{opt.description}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ListBlockEditor({
  block,
  onUpdate,
  lang,
}: {
  block: ContentBlock & { type: 'list' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
  lang: EditLanguage;
}) {
  const addItem = () => onUpdate(block.id, { items: [...block.items, ''] });
  const removeItem = (i: number) => {
    if (block.items.length <= 1) return;
    onUpdate(block.id, { items: block.items.filter((_, idx) => idx !== i) });
  };
  const updateItem = (i: number, value: string) => {
    const updated = [...block.items];
    updated[i] = value;
    onUpdate(block.id, { items: updated });
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
          {(['bullet', 'numbered'] as const).map((s) => (
            <button
              key={s}
              onClick={() => onUpdate(block.id, { style: s })}
              className={`px-2 py-0.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                block.style === s
                  ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
              }`}
            >
              {s === 'bullet' ? <List className="w-3 h-3" /> : <ListOrdered className="w-3 h-3" />}
              {s === 'bullet' ? 'Bullet' : 'Numbered'}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1">
        {block.items.map((item, i) => (
          <div key={i} className="flex items-start gap-2 group/item">
            <span className="text-gray-400 dark:text-gray-500 text-sm mt-1 w-4 text-center flex-shrink-0">
              {block.style === 'bullet' ? '•' : `${i + 1}.`}
            </span>
            <input
              type="text"
              value={item}
              onChange={(e) => updateItem(i, e.target.value)}
              placeholder={lang === 'en' ? 'List item...' : 'Élément de liste...'}
              className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 dark:text-gray-300 placeholder-gray-300 dark:placeholder-gray-600"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addItem();
                }
                if (e.key === 'Backspace' && !item && block.items.length > 1) {
                  e.preventDefault();
                  removeItem(i);
                }
              }}
            />
            <button
              onClick={() => removeItem(i)}
              className="p-0.5 text-gray-300 dark:text-gray-600 hover:text-red-400 opacity-0 group-hover/item:opacity-100 transition-all"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addItem}
        className="mt-2 text-xs text-[#16c47f] hover:text-[#12a868] font-medium flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> Add item
      </button>
    </div>
  );
}

function CalloutBlockEditor({
  block,
  onUpdate,
  lang,
}: {
  block: ContentBlock & { type: 'callout' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
  lang: EditLanguage;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const variants: {
    id: 'info' | 'warning' | 'success' | 'error';
    icon: typeof Info;
    color: string;
    bg: string;
  }[] = [
    { id: 'info', icon: Info, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-500/10' },
    {
      id: 'warning',
      icon: AlertTriangle,
      color: 'text-amber-500',
      bg: 'bg-amber-50 dark:bg-amber-500/10',
    },
    { id: 'success', icon: CheckCircle, color: 'text-[#16c47f]', bg: 'bg-[#16c47f]/5' },
    { id: 'error', icon: XCircle, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-500/10' },
  ];
  const current = variants.find((v) => v.id === block.variant) || variants[0];

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2">
        {variants.map((v) => {
          const VIcon = v.icon;
          return (
            <button
              key={v.id}
              onClick={() => onUpdate(block.id, { variant: v.id })}
              className={`p-1.5 rounded-lg transition-all ${
                block.variant === v.id
                  ? v.bg + ' ' + v.color
                  : 'text-gray-300 dark:text-gray-600 hover:text-gray-500'
              }`}
            >
              <VIcon className="w-4 h-4" />
            </button>
          );
        })}
      </div>
      <TextToolbar
        textareaRef={textareaRef}
        value={block.text}
        onChange={(val) => onUpdate(block.id, { text: val })}
      />
      <div className={`rounded-xl ${current.bg} p-3 flex items-start gap-3`}>
        <current.icon className={`w-5 h-5 ${current.color} flex-shrink-0 mt-0.5`} />
        <textarea
          ref={textareaRef}
          value={block.text}
          onChange={(e) => onUpdate(block.id, { text: e.target.value })}
          placeholder={lang === 'en' ? 'Callout text...' : 'Texte de la note...'}
          className="flex-1 bg-transparent border-none outline-none text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 resize-none min-h-[40px]"
          rows={Math.max(1, block.text.split('\n').length)}
        />
      </div>
    </div>
  );
}

function DividerBlockEditor() {
  return (
    <div className="py-2">
      <hr className="border-gray-100 dark:border-gray-700/50" />
    </div>
  );
}

function QuoteBlockEditor({
  block,
  onUpdate,
  lang,
}: {
  block: ContentBlock & { type: 'quote' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
  lang: EditLanguage;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  return (
    <div className="border-l-4 border-[#16c47f]/30 pl-4">
      <TextToolbar
        textareaRef={textareaRef}
        value={block.text}
        onChange={(val) => onUpdate(block.id, { text: val })}
      />
      <textarea
        ref={textareaRef}
        value={block.text}
        onChange={(e) => onUpdate(block.id, { text: e.target.value })}
        placeholder={lang === 'en' ? 'Quote text...' : 'Texte de la citation...'}
        className="w-full bg-transparent border-none outline-none text-sm italic text-gray-600 dark:text-gray-400 placeholder-gray-300 dark:placeholder-gray-600 resize-none min-h-[40px]"
        rows={Math.max(1, block.text.split('\n').length)}
      />
      <input
        type="text"
        value={block.author || ''}
        onChange={(e) => onUpdate(block.id, { author: e.target.value })}
        placeholder={lang === 'en' ? 'Author (optional)' : 'Auteur (optionnel)'}
        className="w-full bg-transparent border-none outline-none text-xs text-gray-400 dark:text-gray-500 placeholder-gray-300 dark:placeholder-gray-600 mt-1"
      />
    </div>
  );
}

function SpacerBlockEditor({
  block,
  onUpdate,
}: {
  block: ContentBlock & { type: 'spacer' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
}) {
  const sizes: { id: 'sm' | 'md' | 'lg'; label: string; height: string }[] = [
    { id: 'sm', label: 'S', height: 'h-4' },
    { id: 'md', label: 'M', height: 'h-8' },
    { id: 'lg', label: 'L', height: 'h-16' },
  ];

  return (
    <div className="flex items-center gap-3">
      <div
        className={`flex-1 ${sizes.find((s) => s.id === block.size)?.height || 'h-8'} bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-100 dark:border-gray-700/50 flex items-center justify-center`}
      >
        <span className="text-xs text-gray-400">spacer</span>
      </div>
      <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
        {sizes.map((s) => (
          <button
            key={s.id}
            onClick={() => onUpdate(block.id, { size: s.id })}
            className={`px-2 py-0.5 rounded-md text-xs font-medium transition-all ${
              block.size === s.id
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ImageBlockEditor({
  block,
  onUpdate,
  lang,
}: {
  block: ContentBlock & { type: 'image' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
  lang: EditLanguage;
}) {
  return (
    <div className="space-y-2">
      <input
        type="text"
        value={block.url}
        onChange={(e) => onUpdate(block.id, { url: e.target.value })}
        placeholder={lang === 'en' ? 'Image URL...' : "URL de l'image..."}
        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:ring-1 focus:ring-[#16c47f]"
      />
      <input
        type="text"
        value={block.alt}
        onChange={(e) => onUpdate(block.id, { alt: e.target.value })}
        placeholder={lang === 'en' ? 'Alt text (accessibility)...' : 'Texte alternatif...'}
        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:ring-1 focus:ring-[#16c47f]"
      />
      <input
        type="text"
        value={block.caption || ''}
        onChange={(e) => onUpdate(block.id, { caption: e.target.value })}
        placeholder={lang === 'en' ? 'Caption (optional)...' : 'Légende (optionnel)...'}
        className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:ring-1 focus:ring-[#16c47f]"
      />
      {block.url && (
        <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-600">
          <img
            src={block.url}
            alt={block.alt}
            className="max-h-48 w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── Table Block Editor ──

function TableBlockEditor({
  block,
  onUpdate,
}: {
  block: ContentBlock & { type: 'table' };
  onUpdate: (id: string, changes: Partial<ContentBlock>) => void;
}) {
  const update = (changes: Partial<Pick<import('@/types/cms').TableBlock, 'headers' | 'rows'>>) => {
    onUpdate(block.id, changes as Partial<ContentBlock>);
  };

  const updateHeader = (index: number, value: string) => {
    const headers = [...block.headers];
    headers[index] = value;
    update({ headers });
  };

  const updateCell = (rowIndex: number, colIndex: number, value: string) => {
    const rows = block.rows.map((r) => [...r]);
    rows[rowIndex][colIndex] = value;
    update({ rows });
  };

  const addRow = () => {
    const rows = [...block.rows, new Array(block.headers.length).fill('')];
    update({ rows });
  };

  const removeRow = (index: number) => {
    if (block.rows.length <= 1) return;
    const rows = block.rows.filter((_, i) => i !== index);
    update({ rows });
  };

  const addColumn = () => {
    const headers = [...block.headers, `Column ${block.headers.length + 1}`];
    const rows = block.rows.map((r) => [...r, '']);
    update({ headers, rows });
  };

  const removeColumn = (index: number) => {
    if (block.headers.length <= 2) return;
    const headers = block.headers.filter((_, i) => i !== index);
    const rows = block.rows.map((r) => r.filter((_, i) => i !== index));
    update({ headers, rows });
  };

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
          <thead>
            <tr>
              {block.headers.map((h, i) => (
                <th key={i} className="relative group">
                  <input
                    type="text"
                    value={h}
                    onChange={(e) => updateHeader(i, e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white font-semibold outline-none focus:ring-1 focus:ring-[#16c47f] border-r border-gray-200 dark:border-gray-600 last:border-r-0"
                  />
                  {block.headers.length > 2 && (
                    <button
                      onClick={() => removeColumn(i)}
                      className="absolute -top-2 right-0 p-0.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove column"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  )}
                </th>
              ))}
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr key={ri} className="group/row border-t border-gray-200 dark:border-gray-600">
                {row.map((cell, ci) => (
                  <td key={ci}>
                    <input
                      type="text"
                      value={cell}
                      onChange={(e) => updateCell(ri, ci, e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-[#16c47f] border-r border-gray-200 dark:border-gray-600 last:border-r-0"
                    />
                  </td>
                ))}
                <td className="w-8">
                  {block.rows.length > 1 && (
                    <button
                      onClick={() => removeRow(ri)}
                      className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover/row:opacity-100 transition-all"
                      title="Remove row"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={addRow}
          className="text-xs text-[#16c47f] hover:text-[#12a868] font-medium flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add row
        </button>
        <span className="text-gray-300 dark:text-gray-600">|</span>
        <button
          onClick={addColumn}
          className="text-xs text-[#16c47f] hover:text-[#12a868] font-medium flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add column
        </button>
      </div>
    </div>
  );
}
